// Types for MiniApp Bridge responses
export interface MiniAppIdentity {
  Name: string;
  Surname: string;
  TCKN: string;
}

export interface MiniAppPhoneNumber {
  PhoneNumber: string;
}

export interface MiniAppMailAddress {
  Email: string;
}

export interface MiniAppCustomerNumber {
  CustomerNumber: string;
}

export interface MiniAppCommercialCustomerNumber {
  CommercialCustomerNumber: string;
}

export interface MiniAppLocation {
  coords: {
    latitude: number;
    longitude: number;
  };
}

export interface MiniAppPaymentResult {
  success: boolean;
  transactionId?: string;
  message?: string;
}

// Generic response wrapper from shell app
export interface MiniAppResponse<T> {
  isSuccess: boolean;
  retVal: {
    isApproved: boolean;
    funcRetVal: T;
  };
}

// Payment response has a different structure
export interface MiniAppPaymentResponse {
  isSuccess: boolean;
  retVal: MiniAppPaymentResult;
}

// Function names supported by the miniapp bridge
export type MiniAppFunctionName =
  | "GetIdentity"
  | "GetPhoneNumber"
  | "GetMailAddress"
  | "GetCustomerNumber"
  | "GetCommercialCustomerNumber"
  | "GetLocation"
  | "sendPayment";

// Payment parameters
export interface PaymentParams {
  amount: number;
  orderId: string;
}

// Callback handlers type
type CallbackHandler<T> = (result: T) => void;

// Extend Window interface for TypeScript
declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        miniapp?: {
          postMessage: (message: string) => void;
        };
      };
    };
    miniapp?: {
      postMessage: (message: string) => void;
    };
    functionCallback?: (functionName: string, result: string) => void;
  }
}

export class MiniAppBridgeService {
  private pendingCallbacks: Map<MiniAppFunctionName, CallbackHandler<any>> =
    new Map();
  private isInitialized = false;

  constructor() {
    this.initializeCallbackHandler();
  }

  /**
   * Initialize the global callback handler for receiving responses from shell app
   */
  private initializeCallbackHandler(): void {
    if (this.isInitialized) return;

    window.functionCallback = (functionName: string, result: string) => {
      this.handleCallback(functionName as MiniAppFunctionName, result);
    };

    this.isInitialized = true;
  }

  /**
   * Handle callback from shell app
   */
  private handleCallback(functionName: MiniAppFunctionName, result: string): void {
    const handler = this.pendingCallbacks.get(functionName);
    if (handler) {
      try {
        const parsedResult = JSON.parse(result);
        handler(parsedResult);
      } catch (error) {
        console.error("MiniApp Bridge: JSON parsing error:", error);
        handler({ isSuccess: false, error: "JSON parsing failed" });
      }
      this.pendingCallbacks.delete(functionName);
    }
  }

  /**
   * Check if running inside a miniapp WebView
   */
  isInMiniApp(): boolean {
    return this.isIOS() || this.isAndroid();
  }

  /**
   * Check if running on iOS
   */
  isIOS(): boolean {
    return !!(
      window.webkit?.messageHandlers?.miniapp
    );
  }

  /**
   * Check if running on Android
   */
  isAndroid(): boolean {
    return !!(window.miniapp?.postMessage);
  }

  /**
   * Get current platform
   */
  getPlatform(): "ios" | "android" | "web" {
    if (this.isIOS()) return "ios";
    if (this.isAndroid()) return "android";
    return "web";
  }

  /**
   * Send message to shell app
   */
  private postMessage(payload: object): void {
    const message = JSON.stringify(payload);

    if (this.isIOS()) {
      window.webkit!.messageHandlers!.miniapp!.postMessage(message);
    } else if (this.isAndroid()) {
      window.miniapp!.postMessage(message);
    } else {
      console.warn("MiniApp Bridge: Not running in a miniapp environment");
    }
  }

  /**
   * Call a function on the shell app
   */
  private callFunction<T>(
    functionName: MiniAppFunctionName,
    params: object = {}
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      if (!this.isInMiniApp()) {
        reject(new Error("Not running in a miniapp environment"));
        return;
      }

      // Set up callback handler
      this.pendingCallbacks.set(functionName, (result: T) => {
        resolve(result);
      });

      // Set timeout for callback
      setTimeout(() => {
        if (this.pendingCallbacks.has(functionName)) {
          this.pendingCallbacks.delete(functionName);
          reject(new Error(`Timeout waiting for ${functionName} response`));
        }
      }, 30000); // 30 second timeout

      // Send message to shell app
      const payload = {
        callFunction: {
          functionName,
          param: params,
        },
      };

      this.postMessage(payload);
    });
  }

  /**
   * Get user identity (Name, Surname, TCKN)
   */
  async getIdentity(): Promise<MiniAppResponse<MiniAppIdentity>> {
    return this.callFunction<MiniAppResponse<MiniAppIdentity>>("GetIdentity");
  }

  /**
   * Get user phone number
   */
  async getPhoneNumber(): Promise<MiniAppResponse<MiniAppPhoneNumber>> {
    return this.callFunction<MiniAppResponse<MiniAppPhoneNumber>>("GetPhoneNumber");
  }

  /**
   * Get user email address
   */
  async getMailAddress(): Promise<MiniAppResponse<MiniAppMailAddress>> {
    return this.callFunction<MiniAppResponse<MiniAppMailAddress>>("GetMailAddress");
  }

  /**
   * Get customer number
   */
  async getCustomerNumber(): Promise<MiniAppResponse<MiniAppCustomerNumber>> {
    return this.callFunction<MiniAppResponse<MiniAppCustomerNumber>>("GetCustomerNumber");
  }

  /**
   * Get commercial customer number
   */
  async getCommercialCustomerNumber(): Promise<MiniAppResponse<MiniAppCommercialCustomerNumber>> {
    return this.callFunction<MiniAppResponse<MiniAppCommercialCustomerNumber>>("GetCommercialCustomerNumber");
  }

  /**
   * Get user location
   */
  async getLocation(): Promise<MiniAppResponse<MiniAppLocation>> {
    return this.callFunction<MiniAppResponse<MiniAppLocation>>("GetLocation");
  }

  /**
   * Send payment request to shell app
   */
  async sendPayment(amount: number, orderId: string): Promise<MiniAppPaymentResponse> {
    const params: PaymentParams = {
      amount,
      orderId,
    };
    return this.callFunction<MiniAppPaymentResponse>("sendPayment", params);
  }

  // Convenience methods with extracted data

  /**
   * Get user identity data (extracted from response)
   */
  async getUserIdentity(): Promise<MiniAppIdentity | null> {
    try {
      const response = await this.getIdentity();
      if (response.isSuccess && response.retVal.isApproved) {
        return response.retVal.funcRetVal;
      }
      return null;
    } catch (error) {
      console.error("MiniApp Bridge: Failed to get identity:", error);
      return null;
    }
  }

  /**
   * Get user phone number (extracted from response)
   */
  async getUserPhoneNumber(): Promise<string | null> {
    try {
      const response = await this.getPhoneNumber();
      if (response.isSuccess && response.retVal.isApproved) {
        return response.retVal.funcRetVal.PhoneNumber;
      }
      return null;
    } catch (error) {
      console.error("MiniApp Bridge: Failed to get phone number:", error);
      return null;
    }
  }

  /**
   * Get user email (extracted from response)
   */
  async getUserEmail(): Promise<string | null> {
    try {
      const response = await this.getMailAddress();
      if (response.isSuccess && response.retVal.isApproved) {
        return response.retVal.funcRetVal.Email;
      }
      return null;
    } catch (error) {
      console.error("MiniApp Bridge: Failed to get email:", error);
      return null;
    }
  }

  /**
   * Get customer number (extracted from response)
   */
  async getUserCustomerNumber(): Promise<string | null> {
    try {
      const response = await this.getCustomerNumber();
      if (response.isSuccess && response.retVal.isApproved) {
        return response.retVal.funcRetVal.CustomerNumber;
      }
      return null;
    } catch (error) {
      console.error("MiniApp Bridge: Failed to get customer number:", error);
      return null;
    }
  }

  /**
   * Get commercial customer number (extracted from response)
   */
  async getUserCommercialCustomerNumber(): Promise<string | null> {
    try {
      const response = await this.getCommercialCustomerNumber();
      if (response.isSuccess && response.retVal.isApproved) {
        return response.retVal.funcRetVal.CommercialCustomerNumber;
      }
      return null;
    } catch (error) {
      console.error("MiniApp Bridge: Failed to get commercial customer number:", error);
      return null;
    }
  }

  /**
   * Get user location coordinates (extracted from response)
   */
  async getUserLocation(): Promise<{ latitude: number; longitude: number } | null> {
    try {
      const response = await this.getLocation();
      if (response.isSuccess && response.retVal.isApproved) {
        return response.retVal.funcRetVal.coords;
      }
      return null;
    } catch (error) {
      console.error("MiniApp Bridge: Failed to get location:", error);
      return null;
    }
  }

  /**
   * Get all user data at once
   */
  async getAllUserData(): Promise<{
    identity: MiniAppIdentity | null;
    phoneNumber: string | null;
    email: string | null;
    customerNumber: string | null;
    commercialCustomerNumber: string | null;
    location: { latitude: number; longitude: number } | null;
  }> {
    const [identity, phoneNumber, email, customerNumber, commercialCustomerNumber, location] =
      await Promise.all([
        this.getUserIdentity(),
        this.getUserPhoneNumber(),
        this.getUserEmail(),
        this.getUserCustomerNumber(),
        this.getUserCommercialCustomerNumber(),
        this.getUserLocation(),
      ]);

    return {
      identity,
      phoneNumber,
      email,
      customerNumber,
      commercialCustomerNumber,
      location,
    };
  }
}

// Export singleton instance
export const miniAppBridgeService = new MiniAppBridgeService();
